import type { TableOfContents } from '#/domains/landing/types/content';

interface ContentNavProps {
  tableOfContents: TableOfContents;
  /**
   * 바깥 여백. 기본값은 머리말 아래에 단독으로 놓일 때의 값이고,
   * 이미 좌우 여백이 있는 본문 흐름 안에 끼워 넣을 땐 호출부가 비워서 넘긴다.
   */
  className?: string;
  /**
   * 항목별 스크롤 타깃 DOM id (정규화 단계에서 계산됨).
   * v1은 `section-{sectionIndex}`, v2는 섹션의 `id`.
   */
  targets: string[];
}

/**
 * Mystique 3.0 리스트 관용구를 따른다 — 테두리 대신 `surface-basic` 채움 카드,
 * `mds-radius-md`(18px), `body-md-medium` 라벨, `icon-basic-low` 셰브론.
 * (이 서비스는 Tailwind v3라 `@3o3/mystique-zero`를 직접 쓰지 않고 토큰 값만 맞춘다.)
 */
export default function ContentNav({
  tableOfContents,
  targets,
  className = 'mx-6 mb-8',
}: ContentNavProps) {
  const handleClick = (anchorId: string) => {
    const el = document.getElementById(anchorId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <nav
      aria-label={tableOfContents.title}
      className={`rounded-[18px] bg-gray-5 px-5 py-[18px] ${className}`}
    >
      <p className="text-[16px] font-bold leading-[1.5] text-gray-90">
        {tableOfContents.title}
      </p>
      {tableOfContents.layout === 'list' ? (
        <ol className="mt-2 flex flex-col">
          {tableOfContents.items.map((item, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => handleClick(targets[i])}
                className="flex w-full items-center gap-2.5 py-[11px] text-left active:opacity-60"
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-10 text-[12px] font-bold leading-none text-blue-55">
                  {i + 1}
                </span>
                <span className="flex-1 text-[14px] font-medium leading-[1.45] text-gray-80">
                  {item.label}
                </span>
                <ChevronRight />
              </button>
            </li>
          ))}
        </ol>
      ) : (
        <ol className="mt-3 grid grid-cols-2 gap-2">
          {tableOfContents.items.map((item, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => handleClick(targets[i])}
                // h-full: 한 줄 칩이 옆의 두 줄 칩과 같은 높이로 늘어나게
                className="h-full w-full rounded-[10px] bg-white px-3.5 py-3 text-left text-[14px] font-medium leading-[1.4] text-gray-80 transition-colors active:bg-blue-10 active:text-blue-55"
              >
                {item.label}
              </button>
            </li>
          ))}
        </ol>
      )}
    </nav>
  );
}

function ChevronRight() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="shrink-0 text-gray-40"
    >
      <path
        d="M6 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
